import { authAuthorizationHeader } from "./auth"
import type { Mode } from "./types"

export type BandwidthTier = "constrained" | "moderate" | "good"

export interface CallMediaProfile {
  tier: BandwidthTier
  uplinkKbps: number
  downlinkKbps: number
  audioMaxBitrate: number
  video?: {
    width: number
    height: number
    frameRate: number
    maxBitrate: number
    simulcast: boolean
    degradationPreference: RTCDegradationPreference
    priority?: RTCPriorityType
  }
}

interface NetworkInformationLike {
  downlink?: number
  effectiveType?: string
  saveData?: boolean
}

const downloadProbeBytes = 384 * 1024
const uploadProbeBytes = 192 * 1024

export async function estimateCallMediaProfile(mode: Mode, apiBase: string): Promise<CallMediaProfile> {
  const hint = connectionHint()
  const [downloadKbps, uploadKbps] = await Promise.all([
    measureDownloadKbps(apiBase),
    measureUploadKbps(apiBase),
  ])
  const downlinkKbps = firstPositive(downloadKbps, hint.downlinkKbps, 4200)
  const uplinkKbps = firstPositive(uploadKbps, hint.downlinkKbps ? Math.round(hint.downlinkKbps * 0.55) : 0, 2600)
  const limitingKbps = Math.min(uplinkKbps, downlinkKbps)
  const saveData = hint.saveData || hint.effectiveType === "2g" || hint.effectiveType === "slow-2g"
  const tier: BandwidthTier = saveData || limitingKbps < 700
    ? "constrained"
    : limitingKbps < 1800
      ? "moderate"
      : "good"
  if (mode === "voice") {
    return {
      tier,
      uplinkKbps,
      downlinkKbps,
      audioMaxBitrate: tier === "constrained" ? 24_000 : 48_000,
    }
  }
  if (tier === "constrained") {
    return {
      tier,
      uplinkKbps,
      downlinkKbps,
      audioMaxBitrate: 24_000,
      video: videoProfile(640, 480, 20, 450_000, false, "balanced"),
    }
  }
  if (tier === "moderate") {
    return {
      tier,
      uplinkKbps,
      downlinkKbps,
      audioMaxBitrate: 40_000,
      video: videoProfile(960, 720, 24, 1_000_000, true, "maintain-resolution"),
    }
  }
  return {
    tier,
    uplinkKbps,
    downlinkKbps,
    audioMaxBitrate: 48_000,
    video: videoProfile(1280, 960, 30, 2_200_000, true, "maintain-resolution", "high"),
  }
}

function videoProfile(
  landscapeWidth: number,
  landscapeHeight: number,
  frameRate: number,
  maxBitrate: number,
  simulcast: boolean,
  degradationPreference: RTCDegradationPreference,
  priority?: RTCPriorityType,
): NonNullable<CallMediaProfile["video"]> {
  const portrait = preferPortraitCapture()
  return {
    width: portrait ? landscapeHeight : landscapeWidth,
    height: portrait ? landscapeWidth : landscapeHeight,
    frameRate,
    maxBitrate,
    simulcast,
    degradationPreference,
    priority,
  }
}

function preferPortraitCapture(): boolean {
  if (typeof window === "undefined") return false
  return window.innerHeight > window.innerWidth && Boolean(window.matchMedia?.("(pointer: coarse)").matches)
}

function connectionHint(): { downlinkKbps: number; effectiveType: string; saveData: boolean } {
  const nav = navigator as Navigator & { connection?: NetworkInformationLike; mozConnection?: NetworkInformationLike; webkitConnection?: NetworkInformationLike }
  const connection = nav.connection || nav.mozConnection || nav.webkitConnection
  return {
    downlinkKbps: connection?.downlink ? Math.round(connection.downlink * 1000) : 0,
    effectiveType: connection?.effectiveType || "",
    saveData: Boolean(connection?.saveData),
  }
}

async function measureDownloadKbps(apiBase: string): Promise<number> {
  try {
    const started = performance.now()
    const authorization = authAuthorizationHeader()
    const response = await fetch(`${apiBase}/network/probe/download?bytes=${downloadProbeBytes}&t=${Date.now()}`, {
      cache: "no-store",
      headers: authorization ? { Authorization: authorization } : {},
    })
    if (!response.ok) return 0
    const buffer = await response.arrayBuffer()
    return kbps(buffer.byteLength, performance.now() - started)
  } catch {
    return 0
  }
}

async function measureUploadKbps(apiBase: string): Promise<number> {
  try {
    const payload = new Uint8Array(uploadProbeBytes)
    for (let index = 0; index < payload.length; index += 1) payload[index] = (index * 31 + 17) % 251
    const started = performance.now()
    const authorization = authAuthorizationHeader()
    const response = await fetch(`${apiBase}/network/probe/upload?t=${Date.now()}`, {
      method: "POST",
      cache: "no-store",
      headers: {
        ...(authorization ? { Authorization: authorization } : {}),
        "Content-Type": "application/octet-stream",
      },
      body: payload,
    })
    if (!response.ok) return 0
    return kbps(payload.byteLength, performance.now() - started)
  } catch {
    return 0
  }
}

function kbps(bytes: number, elapsedMs: number): number {
  if (bytes <= 0 || elapsedMs <= 0) return 0
  return Math.round((bytes * 8) / elapsedMs)
}

function firstPositive(...values: number[]): number {
  return values.find((value) => Number.isFinite(value) && value > 0) || 0
}
